import GoogleMapsApi from "./GoogleMapsApi";
import GoogleMapsStyles from "./GoogleMapsStyles";
import ScrollListener from '../../../util/ScrollListener';

interface RWDXMapLocation {
    lat: number;
    lng: number;
    title: string;
}

interface RWDXMapData {
    lat: number;
    lng: number;
    zoom: number;
    locations: RWDXMapLocation[];
}

const mobile = () => window.innerWidth <= 680;
const tablet = () => window.innerWidth > 680 && window.innerWidth <= 1024;


class GoogleMaps {
    static MARKER_WIDTH = 60;
    static MARKER_HEIGHT = 80;
    private mapApi : GoogleMapsApi;
    private map : google.maps.Map;
    private markers : google.maps.Marker[] = [];
    private scrollListener : ScrollListener;
    private data : RWDXMapData;
    private resizeTimeout: ReturnType<typeof setTimeout> | undefined;

    private currentView: 'mobile' | 'tablet' | 'desktop';

    desktopData: {[key: string]: number} = {
        lat: parseFloat(this.mapEl.dataset.rwdxMapsCenterLat),
        lng: parseFloat(this.mapEl.dataset.rwdxMapsCenterLng),
        zoom: parseFloat(this.mapEl.dataset.rwdxMapsZoom)
    }

    tabletData: {[key: string]: number} = {
        lat: 52.626565859483264,
        lng: 1.3106912511635167,
        zoom: 16.5
    }

    mobileData: {[key: string]: number} = {
        lat: 52.62715164129294,
        lng: 1.3087361895525695,
        zoom: 16
    }

    constructor(private mapEl : HTMLElement) {
        this.mapApi = new GoogleMapsApi("AIzaSyCwmFgEiA_BGogZXQrSxZAjuw0hAVzwVOY");

        this.data = {
            lat: this.desktopData.lat,
            lng: this.desktopData.lng,
            zoom: this.desktopData.zoom,
            locations: []
        };

        this.getViewType();

        const locations = parseInt(this.mapEl.dataset.rwdxMapsLocations);

        for (let i = 1; i <= locations; i++) {
            this.data.locations.push({
                lat: parseFloat(this.mapEl.getAttribute('data-rwdx-maps-location-' + i + '-lat')),
                lng: parseFloat(this.mapEl.getAttribute('data-rwdx-maps-location-' + i + '-lng')),
                title: this.mapEl.getAttribute('data-rwdx-maps-location-' + i + '-title') || 'Map'
            })
        }

        window.addEventListener('resize', this.handleResize.bind(this));

        this.scrollListener = new ScrollListener();
    }

    init() {
        this.scrollListener.attachListener(this.checkBounds.bind(this));
    }

    checkBounds() {
        const bounds = this.mapEl.getBoundingClientRect();

        if (bounds.top <= window.innerHeight + window.innerHeight / 2) {
            this.mapApi.load().then(() => {
                this.renderMap()
            });

            this.scrollListener.removeListener();
            return true;
        }

        return false;
    }

    handleResize() {
        this.getViewType();

        clearTimeout(this.resizeTimeout);

        this.resizeTimeout = setTimeout(() => {
            this.renderMap();
        }, 300);
    }

    getViewType() {
        if (mobile()) {
            this.data = {
                ...this.data,
                lat:this.mobileData.lat,
                lng: this.mobileData.lng,
                zoom: this.mobileData.zoom
            };
        } else if (tablet()) {
            this.data = {
                ...this.data,
                lat:this.tabletData.lat,
                lng: this.tabletData.lng,
                zoom: this.tabletData.zoom
            };
        } else {
            this.data = {
                ...this.data,
                lat:this.desktopData.lat,
                lng: this.desktopData.lng,
                zoom: this.desktopData.zoom
            };
        }
    }

    renderMap() {
        const options = {
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            styles: GoogleMapsStyles,
            zoom: this.data.zoom,
            center: {
                lat: this.data.lat,
                lng: this.data.lng
            },
            disableDefaultUI: true,
        };

        this.map = new window.google.maps.Map(this.mapEl, options);
        this.renderMarker();
    }

    renderMarker() {
        type LocationType = 'Cafe' | 'Parking' | 'Station';

        const icons : Record<LocationType, string>  = {
            Cafe: "/img/svg/map-pin-HOME.svg",
            Parking: "/img/svg/map-pin-PARKING.svg",
            Station: "/img/svg/map-pin-STATION.svg"
        }

        const sizes : Record<LocationType, { width: number; height: number }>  = {
            Cafe: {
                width: mobile() ? 68 : 85,
                height: mobile() ? 88 : 120
            },
            Parking: {
                width: mobile() ? 48 : 65,
                height: mobile() ? 58 : 90
            },
            Station: {
                width: mobile() ? 48 : 65,
                height: mobile() ? 58 : 90
            }
        }

        for (const location of this.data.locations) {

            const locationKey = location.title as LocationType;

            const icon = {
                url: icons[locationKey],
                scaledSize: new window.google.maps.Size(sizes[locationKey].width, sizes[locationKey].height),
                origin: new window.google.maps.Point(0, 0),
                anchor: new window.google.maps.Point(GoogleMaps.MARKER_WIDTH / 2, GoogleMaps.MARKER_HEIGHT)
            }

            this.markers.push(new google.maps.Marker({
                position: new google.maps.LatLng(location.lat, location.lng),
                map: this.map,
                icon,
                title: location.title,
                animation: google.maps.Animation.DROP
            }))
        }
    }
}

export default GoogleMaps;